import { useTranslation } from 'react-i18next';

interface Props {
  /** Remaining free plays today. 0 means the daily allowance is spent. */
  remaining: number;
  limit: number;
  /** True when the next play requires watching a pre-roll ad. */
  adRequired?: boolean;
  /** Dismiss + keep watching (plays the ad first when adRequired). */
  onContinue?: () => void;
  /** Go to the subscribe / payment page. */
  onSubscribe: () => void;
  /** Leave the watch page. */
  onBack?: () => void;
}

/**
 * Shown to Limited (recruitee, unpaid) users on a watch page. Unlike a hard
 * paywall, Limited users are NEVER blocked — once their daily free plays are
 * spent they can keep watching after a short ad, or subscribe to remove ads and
 * the daily cap. Reuses the `.join-prompt` modal styling.
 */
export function SubscriptionModal({ remaining, limit, adRequired, onContinue, onSubscribe, onBack }: Props): JSX.Element {
  const { t } = useTranslation();
  const spent = adRequired || remaining <= 0;
  return (
    <div className="join-prompt__backdrop" role="presentation">
      <div className="join-prompt" role="dialog" aria-modal="true" aria-labelledby="sub-modal-title">
        <h2 id="sub-modal-title" className="join-prompt__title">
          {spent
            ? t('billing.freePlaysModal.titleSpent')
            : t('billing.freePlaysModal.titleRemaining', { count: remaining })}
        </h2>
        <p className="join-prompt__body">
          {spent
            ? t('billing.freePlaysModal.bodySpent')
            : t('billing.freePlaysModal.bodyRemaining', { limit })}
        </p>
        <div className="join-prompt__actions">
          <button type="button" className="btn" onClick={onSubscribe} data-testid="sub-modal-subscribe">
            {t('billing.freePlaysModal.subscribe')}
          </button>
          {onContinue && (
            <button type="button" className="btn-ghost" onClick={onContinue} data-testid="sub-modal-continue">
              {spent ? t('billing.freePlaysModal.watchWithAd') : t('billing.freePlaysModal.watchNow')}
            </button>
          )}
          {spent && onBack && (
            <button type="button" className="btn-ghost" onClick={onBack} data-testid="sub-modal-back">
              {t('billing.freePlaysModal.goBack')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
